import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';

// 创建脉冲动画效果
const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.15);
  }
  70% {
    box-shadow: 0 0 0 4px rgba(255, 255, 255, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
  }
`;

// 创建数字变化动画效果
const countChange = keyframes`
  0% {
    transform: translateY(-3px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
`;

// 创建旋转动画
const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

// 旋转图标样式
const RotatingIcon = styled(AutorenewIcon)(({ theme }) => ({
  animation: `${rotate} 1.5s linear infinite`,
  fontSize: '14px',
  marginRight: '4px',
  color: 'rgba(255, 255, 255, 0.9)'
}));

// 字数统计标签样式 - 简洁精致设计
const StyledWordCountBadge = styled(Paper)(({ theme, position = 'header', fullWidth = false }) => {
  // 基础样式
  const baseStyle = {
    backgroundColor: 'rgba(25, 118, 210, 0.85)',
    color: 'white',
    padding: '6px 12px',
    borderRadius: '16px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.85), rgba(21, 101, 192, 0.75))',
    backdropFilter: 'blur(5px)',
    animation: `${pulse} 3s infinite`,
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
    '&:hover': {
      boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)',
    },
    minWidth: fullWidth ? 'auto' : '90px',
    maxWidth: 'none',
    height: '32px',
    whiteSpace: 'nowrap',
  };

  // 位置相关样式
  const positionStyles = {
    header: {
      ...baseStyle,
      position: 'relative',
      width: 'auto',
      height: '100%',
      minWidth: 0,
    },
    floating: {
      ...baseStyle,
      position: 'fixed',
      bottom: 20,
      right: 20,
      zIndex: 1000,
    }
  };

  return positionStyles[position] || positionStyles.floating;
});

// 数字显示样式
const WordCountNumber = styled('span')(({ theme, animate }) => ({
  fontWeight: '600',
  fontSize: '0.95em',
  display: 'inline-block',
  animation: animate ? `${countChange} 0.3s ease-out` : 'none',
  marginLeft: '3px',
  marginRight: '3px',
  color: '#ffffff'
}));

/**
 * 简化的字数统计标签组件
 * @param {Object} props 组件属性
 * @param {number} props.wordCount 当前字数
 * @param {boolean} props.visible 是否可见
 * @param {string} props.position 位置 ('header' 或 'floating')
 * @param {boolean} props.isGenerating 是否正在生成
 * @param {boolean} props.fullWidth 是否全宽显示（用于手机端）
 * @returns {JSX.Element} 字数统计标签组件
 */
const WordCountBadge = ({
  wordCount = 0,
  visible = true,
  position = 'header',
  isGenerating = false,
  fullWidth = false
}) => {
  // 使用React.useState记录上一次的字数，用于动画
  const [prevCount, setPrevCount] = React.useState(wordCount);
  const [animate, setAnimate] = React.useState(false);

  // 当字数变化时触发动画
  React.useEffect(() => {
    if (wordCount !== prevCount) {
      setAnimate(true);
      setPrevCount(wordCount);

      // 动画结束后重置
      const timer = setTimeout(() => {
        setAnimate(false);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [wordCount, prevCount]);

  if (!visible) return null;

  // 渲染统一样式的字数统计标签
  return (
    <StyledWordCountBadge
      elevation={2}
      position={position}
      fullWidth={fullWidth}
    >
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1,
      }}>
        {isGenerating ? (
          <RotatingIcon />
        ) : (
          <TextSnippetIcon sx={{ fontSize: '14px', mr: 0.5, opacity: 0.9 }} />
        )}
        <Typography variant="body2" sx={{ 
          fontWeight: '500', 
          fontSize: '0.85rem', 
          letterSpacing: '0.01em',
          opacity: 0.95
        }}>
          已生成<WordCountNumber animate={animate}>{wordCount.toLocaleString()}</WordCountNumber>字
        </Typography>
      </Box>
    </StyledWordCountBadge>
  );
};

export default WordCountBadge; 